import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useCreateFolderByTypologyAsync,
  useUpdateCrossingRequestedDateAsync,
} from '@/features/customsPass/createNewRequest/apis';
import { useFolderTypologyState } from '@/features/shared/hooks';
import { CROSSING_DIRECTION_COUNTRY } from '@/features/shared/constants';

type NewRequestForm = Partial<{
  direction: CROSSING_DIRECTION_COUNTRY;
  crossingRequestedDate: string;
}>;

export const useCreateNewRequest = ({ direction, crossingRequestedDate }: NewRequestForm) => {
  const [isCreateNewRequestInError, setIsCreateNewRequestInError] = React.useState(false);

  const { folderTypologyId } = useFolderTypologyState();

  const navigate = useNavigate();

  const { mutate: createFolderByTypology, isLoading: isCreateFolderByTypologyPending } =
    useCreateFolderByTypologyAsync();
  const { mutate: updateCrossingRequestedDate, isLoading: isUpdateCrossingRequestedDatePending } =
    useUpdateCrossingRequestedDateAsync();

  const handleCreateNewFolder = useCallback(() => {
    const data = { direction, folderTypologyId };
    createFolderByTypology(data, {
      onSuccess: ({ data: folderId }) => {
        if (crossingRequestedDate) {
          updateCrossingRequestedDate(
            {
              folderId,
              folderTypologyId,
              crossingRequestedDate,
            },
            {
              onSuccess: () => {
                navigate(`/folders/${folderId}/zendesk-information`);
              },
            },
          );
        } else {
          navigate(`/folders/${folderId}/zendesk-information`);
        }
      },
      onError: () => {
        setIsCreateNewRequestInError(true);
      },
    });
  }, [
    direction,
    folderTypologyId,
    crossingRequestedDate,
    createFolderByTypology,
    updateCrossingRequestedDate,
    navigate,
  ]);

  const handleCloseToaster = useCallback(() => {
    setIsCreateNewRequestInError(false);
  }, []);

  return {
    isUpdateCrossingRequestedDatePending,
    isCreateFolderByTypologyPending,
    isCreateNewRequestInError,
    handleCloseToaster,
    handleCreateNewFolder,
  };
};
